import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type {
  ActiveLanguagesCollection,
  DomainsCollection,
  LanguagesCollection,
} from '@freelancer/datastore/collections';
import { Localization } from '@freelancer/localization';
import { Location } from '@freelancer/location';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkHoverColor, LinkUnderline } from '@freelancer/ui/link';
import { TextSize } from '@freelancer/ui/text';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  LanguageSwitcherLinkDisplay,
  LanguageSwitcherTheme,
} from './language-switcher.types';

@Component({
  standalone: false,
  selector: 'fl-language-switcher-link',
  template: `
    <fl-link
      [flTrackingLabel]="
        useLanguageSwitcherModal
          ? 'openLanguageSwitcherModal'
          : 'goToChooseCountry'
      "
      [link]="useLanguageSwitcherModal ? undefined : '/choose-your-country'"
      [size]="TextSize.INHERIT"
      [color]="linkColor"
      [hoverColor]="LinkHoverColor.DEFAULT"
      [underline]="linkUnderline"
      [display]="'flex'"
      [flexAlign]="'center'"
      (click)="handleClick()"
    >
      <fl-icon
        [class.mr-xsmall]="display === LanguageSwitcherLinkDisplay.FULL"
        [class.mr-xxsmall]="display === LanguageSwitcherLinkDisplay.ACRONYM"
        [class.mobile:hide]="display === LanguageSwitcherLinkDisplay.FULL"
        [name]="'ui-globe'"
        [color]="IconColor.INHERIT"
        [useIconFont]="true"
        [size]="IconSize.MID"
      ></fl-icon>
      <span class="Label">
        @if (display === LanguageSwitcherLinkDisplay.FULL) {
          {{ (domainName$ | flAsync) || 'Unknown Domain' }} /
          {{ language$ | flAsync }}
        } @else {
          {{ language$ | flAsync }}
        }
      </span>
    </fl-link>
  `,
  styleUrls: ['./language-switcher-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitcherLinkComponent implements OnInit {
  private localization = inject(Localization);
  private datastore = inject(Datastore);
  private location = inject(Location);

  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkHoverColor = LinkHoverColor;
  LinkUnderline = LinkUnderline;
  LanguageSwitcherTheme = LanguageSwitcherTheme;
  LanguageSwitcherLinkDisplay = LanguageSwitcherLinkDisplay;
  TextSize = TextSize;

  language$: Observable<string>;
  domainName$: Observable<string | undefined>;

  @Input() color: LanguageSwitcherTheme;
  @Input() display: LanguageSwitcherLinkDisplay =
    LanguageSwitcherLinkDisplay.FULL;
  @Input() isLoggedIn: boolean;
  @Input() useLanguageSwitcherModal = false;

  @Output() languageSwitcherClick = new EventEmitter<void>();

  get linkColor(): LinkColor {
    return this.color === LanguageSwitcherTheme.LIGHT
      ? LinkColor.LIGHT
      : LinkColor.INHERIT;
  }

  get linkUnderline(): LinkUnderline {
    return this.color === LanguageSwitcherTheme.LIGHT
      ? LinkUnderline.NEVER
      : LinkUnderline.HOVER;
  }

  handleClick(): void {
    if (this.useLanguageSwitcherModal) {
      this.languageSwitcherClick.emit();
    }
  }

  ngOnInit(): void {
    const languageDocument$ = this.isLoggedIn
      ? this.datastore
          .document<LanguagesCollection>(
            'languages',
            this.localization.languageCode,
          )
          .valueChanges()
      : this.datastore
          .document<ActiveLanguagesCollection>(
            'activeLanguages',
            this.localization.languageCode,
          )
          .valueChanges();

    if (this.display === LanguageSwitcherLinkDisplay.ACRONYM) {
      this.language$ = languageDocument$.pipe(
        map(language => language.code.toUpperCase()),
      );
      return;
    }

    const domainName = this.location.hostname;

    this.domainName$ = this.datastore
      .collection<DomainsCollection>('domains', query =>
        // slice off the `www` because the DB doesn't have it.
        // FIXME: T267853 - move the hack to the endpoint
        query.where('domainName', '==', domainName.replace(/^www\./, '')),
      )
      .valueChanges()
      .pipe(map(domains => (domains.length > 0 ? domains[0].name : undefined)));

    this.language$ = languageDocument$.pipe(map(language => language.name));
  }
}
